import React from "react";

/**
 * Editorial card surface with a 1px gold hairline accent on hover.
 *
 * The `tone` prop picks which surface tier the card sits on:
 *   light   — white card on cream/offwhite backgrounds (marketplace, breeders)
 *   cream   — warm-paper card on white backgrounds (about, pricing)
 *   dark    — obsidian card with subtle inset highlight (dashboard, admin)
 *
 * `interactive` adds a slow lift + gold-glow ring on hover, used on every
 * clickable card (dog tile, kennel tile, listing). Passes through any
 * remaining props (onClick, href via wrapper, data-testid, etc).
 *
 * Usage:
 *   <LuxuryCard interactive onClick={...}>
 *     <DogPreview />
 *   </LuxuryCard>
 */
export const LuxuryCard = React.forwardRef(function LuxuryCard(
  { children, tone = "light", interactive = false, className = "", ...rest },
  ref,
) {
  const toneClass = {
    light: "bg-white border-[#EAEAEA] shadow-luxury-light text-[#0E0E0E]",
    cream: "bg-[#F4F0E6] border-[#E2DAC2] shadow-luxury-light text-[#0E0E0E]",
    dark:  "bg-[#0E0E0E] border-white/10 shadow-luxury-dark text-white",
  }[tone];

  const interactiveClass = interactive
    ? "transition-all duration-500 ease-out hover:-translate-y-0.5 hover:border-[#C9A227]/55 hover:shadow-gold-glow cursor-pointer"
    : "";

  return (
    <div
      ref={ref}
      data-testid="luxury-card"
      data-tone={tone}
      className={`rounded-xl border ${toneClass} ${interactiveClass} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
});

export default LuxuryCard;
